import React, { useContext, useMemo } from "react"
import { Linking, Platform, StyleSheet, View } from "react-native"
import { theme } from "@common/theme/theme"
import TextM from "@common/components/text/TextM"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import { DimensionsContext } from "@common/utils/mobileUtils"
import { CongratsTemplate } from "@common/templates/CongratsTemplate"
import { LinearGradient } from "expo-linear-gradient"
import TouchableRippleM from "@common/components/button/TouchableRippleM"
import LinkTextM from "@common/components/text/LinkTextM"
import { getCalendlyBaseUrl } from "@data/utility/Utils"

const WHATSAPP_URL = "https://wa.me/message/FCOBQ6PCPMBJC1"

const StepItem = ({ number, title, description }) => (
    <View style={styles.stepCard}>
        <View style={styles.stepNumber}>
            <TextM fontSize={16} fontWeight="Bold" color={theme.colors.white}>
                {number}
            </TextM>
        </View>
        <View style={styles.stepContent}>
            <TextM
                fontSize={15}
                fontWeight="Bold"
                color={theme.colors.primary}
                wrap
            >
                {title}
            </TextM>
            <TextM
                fontSize={13}
                color={theme.colors.grey}
                style={{ lineHeight: 20 }}
                wrap
            >
                {description}
            </TextM>
        </View>
    </View>
)

const StagePreInscriptionConfirmationScreen = ({ route }) => {
    const dimensions = useContext(DimensionsContext)
    const containerWidth = dimensions?.width

    const firstname = route.params?.firstname ?? ""
    const lastname = route.params?.lastname ?? ""
    const email = route.params?.email ?? ""
    const phone = route.params?.phone ?? ""

    const calendlyUrl = useMemo(() => {
        const params = new URLSearchParams()
        if (firstname) params.append("first_name", firstname)
        if (lastname) params.append("last_name", lastname)
        if (email) params.append("email", email)
        if (phone) params.append("a1", phone)
        params.append("a2", "Pré-inscription stage vacances")
        // Lead/essai context → Leads channel (falls back to historical URL).
        const baseUrl = getCalendlyBaseUrl("trial-done")
        const separator = baseUrl.includes("?") ? "&" : "?"
        return `${baseUrl}${separator}${params.toString()}`
    }, [firstname, lastname, email, phone])

    const handleCalendlyPress = () => {
        if (Platform.OS === "web") {
            window.open(calendlyUrl, "_blank")
        } else {
            Linking.openURL(calendlyUrl)
        }
    }

    return (
        <CongratsTemplate title="Pré-inscription confirmée !">
            {/* Confirmation card */}
            <View style={styles.confirmCard}>
                <FontAwesome5
                    name="check-circle"
                    solid
                    size={28}
                    color={theme.colors.success}
                />
                <TextM
                    fontSize={16}
                    fontWeight="Bold"
                    color={theme.colors.success}
                    wrap
                    style={{ textAlign: "center" }}
                >
                    La place de {firstname} est réservée
                </TextM>
                <TextM
                    fontSize={13}
                    color={theme.colors.grey}
                    wrap
                    style={{ textAlign: "center", lineHeight: 20 }}
                >
                    Votre pré-inscription de 1€ a bien été enregistrée.{"\n"}
                    Ce montant sera déduit du montant final du stage.
                </TextM>
            </View>

            {/* Next steps */}
            <View style={styles.stepsSection}>
                <TextM
                    fontSize={18}
                    fontWeight="Bold"
                    color={theme.colors.primary}
                    style={{ textAlign: "center", marginBottom: 16 }}
                >
                    Prochaines étapes
                </TextM>

                <StepItem
                    number="1"
                    title="Prenez rendez-vous maintenant"
                    description="Échangez avec un conseiller pédagogique pour organiser le stage sur mesure."
                />
                <StepItem
                    number="2"
                    title="Programme personnalisé"
                    description="Un programme 100% adapté aux besoins de votre enfant sera préparé."
                />
                <StepItem
                    number="3"
                    title="Début du stage"
                    description="Votre enfant commence son stage de révision avec son tuteur dédié."
                />
            </View>

            {/* Calendly CTA */}
            <TouchableRippleM
                onPress={handleCalendlyPress}
                style={styles.ctaButton}
            >
                <LinearGradient
                    colors={[theme.colors.primary, theme.colors.primaryDark]}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 1 }}
                    style={styles.ctaGradient}
                >
                    <FontAwesome5
                        name="calendar-alt"
                        solid
                        size={18}
                        color={theme.colors.white}
                    />
                    <TextM
                        fontSize={18}
                        fontWeight="Bold"
                        color={theme.colors.white}
                    >
                        Prendre rendez-vous
                    </TextM>
                </LinearGradient>
            </TouchableRippleM>

            {/* Contact */}
            <View
                style={[
                    styles.contactCard,
                    { width: containerWidth, alignSelf: "center" },
                ]}
            >
                <TextM
                    fontSize={14}
                    fontWeight="Bold"
                    color={theme.colors.primary}
                    style={{ textAlign: "center", marginBottom: 8 }}
                >
                    Une question ?
                </TextM>
                <TextM fontSize={14} color={theme.colors.grey} wrap>
                    N&apos;hésitez pas à nous contacter{" "}
                    <LinkTextM
                        url="mailto:support@masteur.com"
                        style={{ color: theme.colors.link }}
                    >
                        par email
                    </LinkTextM>
                    {" "}ou{" "}
                    <LinkTextM
                        url={WHATSAPP_URL}
                        style={{ color: theme.colors.link }}
                    >
                        par WhatsApp
                    </LinkTextM>
                </TextM>
            </View>
        </CongratsTemplate>
    )
}

const styles = StyleSheet.create({
    confirmCard: {
        backgroundColor: theme.colors.white,
        padding: 24,
        borderRadius: 16,
        borderWidth: 1,
        borderColor: theme.colors.successLighter ?? theme.colors.light,
        width: "100%",
        alignItems: "center",
        gap: 10,
    },
    stepsSection: {
        width: "100%",
        paddingTop: 30,
    },
    stepCard: {
        width: "100%",
        backgroundColor: theme.colors.white,
        padding: 16,
        borderRadius: 14,
        marginBottom: 12,
        flexDirection: "row",
        alignItems: "flex-start",
        borderWidth: 1,
        borderColor: theme.colors.lightMore,
    },
    stepNumber: {
        width: 30,
        height: 30,
        borderRadius: 15,
        backgroundColor: theme.colors.primary,
        alignItems: "center",
        justifyContent: "center",
        marginRight: 14,
        marginTop: 2,
    },
    stepContent: {
        flex: 1,
        gap: 2,
    },
    ctaButton: {
        width: "100%",
        borderRadius: 15,
        overflow: "hidden",
        marginTop: 10,
    },
    ctaGradient: {
        paddingVertical: 18,
        paddingHorizontal: 40,
        alignItems: "center",
        flexDirection: "row",
        justifyContent: "center",
        gap: 10,
    },
    contactCard: {
        backgroundColor: theme.colors.primaryLighter,
        padding: 20,
        borderRadius: 16,
        marginTop: 30,
        marginBottom: 30,
        alignItems: "center",
        borderWidth: 1,
        borderColor: theme.colors.primaryLighter,
    },
})

export default StagePreInscriptionConfirmationScreen
